// Normalisers for the /reviews and /reviews/summary payloads. Field names
// aren't pinned down anywhere beyond the Postman collection's prose
// ("rating (1-5), comment (min 20, max 1000)…"), so every likely alias is
// tolerated the same way bookingDetailsData.js/astrologerDetailsData.js do
// for their own loosely-typed upstream payloads.

/** Unwrap the Laravel `{ status, data }` envelope (or pass a bare value through). */
function unwrap(res) {
  if (res && typeof res === 'object' && 'data' in res && res.data !== undefined) return res.data
  return res
}

/** Coerce a value into a plain list — handles arrays, envelopes, length-aware
 * paginators nested under `data`, and this endpoint's actual live shape:
 * `{ status, message, data: Review[], meta: {...} }`, where the pagination
 * info is a SIBLING of `data`, not nested inside it. */
export function unwrapReviewList(res) {
  if (res && typeof res === 'object' && Array.isArray(res.data) && res.meta && typeof res.meta === 'object') {
    const m = res.meta
    return {
      items: res.data,
      currentPage: m.current_page ?? 1,
      lastPage: m.last_page ?? 1,
      total: m.total ?? res.data.length,
      perPage: m.per_page ?? res.data.length,
    }
  }
  const d = unwrap(res)
  if (Array.isArray(d)) {
    return { items: d, currentPage: 1, lastPage: 1, total: d.length, perPage: d.length }
  }
  if (d && Array.isArray(d.data)) {
    return {
      items: d.data,
      currentPage: d.current_page ?? 1,
      lastPage: d.last_page ?? 1,
      total: d.total ?? d.data.length,
      perPage: d.per_page ?? d.data.length,
    }
  }
  return { items: [], currentPage: 1, lastPage: 1, total: 0, perPage: 0 }
}

/** Unwrap GET /reviews/summary → { totalReviews, averageRating, breakdown }. */
export function unwrapReviewSummary(res) {
  const d = unwrap(res) || {}
  const breakdown = d.rating_breakdown || d.distribution || d.breakdown || null
  return {
    totalReviews: Number(d.total_reviews ?? d.reviews_count ?? d.count ?? 0) || 0,
    averageRating: Number(d.average_rating ?? d.rating ?? 0) || 0,
    breakdown: breakdown && typeof breakdown === 'object' ? breakdown : null,
  }
}

/** 1..5 → count, computed from whatever page of reviews is currently loaded
 * — used when the summary endpoint doesn't carry a `rating_breakdown` and no
 * "give me the exact distribution" endpoint exists. Callers should label
 * this as based on loaded reviews when the summary's total exceeds the
 * loaded count, rather than presenting it as an exact global distribution. */
export function computeBreakdownFromList(items) {
  const breakdown = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 }
  items.forEach((r) => {
    const star = Math.round(Number(r.rating) || 0)
    if (star >= 1 && star <= 5) breakdown[star] += 1
  })
  return breakdown
}

const authorOf = (review) => review?.user || review?.reviewer || {}

export function reviewAuthorName(review) {
  const a = authorOf(review)
  const full = [a.first_name, a.last_name].filter(Boolean).join(' ').trim()
  return a.name || full || 'A verified client'
}

export function reviewAuthorInitial(review) {
  return reviewAuthorName(review).charAt(0).toUpperCase()
}

export function reviewAuthorAvatar(review) {
  const a = authorOf(review)
  return a.avatar_url || a.avatar || a.image || null
}

export function reviewAuthorId(review) {
  const a = authorOf(review)
  return a.id ?? review?.user_id ?? null
}

export function reviewText(review) {
  return review?.comment || review?.review || review?.text || review?.body || ''
}

export function reviewIsVerified(review) {
  return Boolean(review?.is_verified ?? review?.verified ?? review?.verified_purchase ?? review?.is_verified_purchase)
}

/** Mean of whatever page of reviews is currently loaded — paired with
 * computeBreakdownFromList() as the fallback source of truth when
 * GET /reviews/summary isn't available (see useReviewsAggregate). */
export function computeAverageFromList(items) {
  if (!items.length) return 0
  const sum = items.reduce((acc, r) => acc + (Number(r.rating) || 0), 0)
  return sum / items.length
}

export function reviewHelpfulCount(review) {
  return Number(review?.helpful_count ?? review?.helpful ?? 0) || 0
}

export function reviewImages(review) {
  const list = review?.images || review?.media
  if (!Array.isArray(list)) return []
  return list
    .map((item) => (typeof item === 'string' ? item : item?.original_url || item?.url))
    .filter(Boolean)
}

/** "2026-06-20T10:00:00Z" → "20 Jun 2026". Falls back to the raw string, then blank. */
export function reviewDateLabel(review) {
  const raw = review?.created_at || review?.date
  if (!raw) return ''
  const d = new Date(raw)
  if (Number.isNaN(d.getTime())) return String(raw)
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
}

/** The signed-in user's own review within a loaded list, if any — the only
 * way to detect "already reviewed" / drive an Edit affordance without a
 * dedicated "my review" endpoint. */
export function findMyReview(items, userId) {
  if (!userId) return null
  return items.find((r) => String(reviewAuthorId(r)) === String(userId)) || null
}

export const SORT_OPTIONS = [
  { value: 'recent', label: 'Most Recent' },
  { value: 'rating_high', label: 'Highest Rating' },
  { value: 'rating_low', label: 'Lowest Rating' },
  { value: 'helpful', label: 'Most Helpful' },
]

export const RATING_FILTERS = [0, 5, 4, 3, 2, 1]
